import React, { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  styled,
  MenuItem,
  Backdrop,
  Menu
} from "@mui/material"
import { useTheme } from "@mui/system"
import { Droppable, Draggable } from "react-beautiful-dnd"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import TableNoRecords from "components/TableNoRecords"
import { podsColumns } from "helpers/constant"
import { useSelector, useDispatch } from "react-redux"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { markParticipantAsDefaulterAction, getLeagueDataAction } from "redux/DMV/actions"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import ProfileDisplayer from "components/ProfileDisplayer"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: "#1B1F21",
  borderRadius: "8px !important",
  borderCollapse: "collapse !important",
  padding: "10px !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0",
    backgroundColor: "transparent",
    borderBottom: `1px solid rgba(249, 242, 242, 0.1)`,
    boxSizing: "border-box",
    "&:first-of-type": {
      paddingLeft: "0"
    }
  },
  "& .MuiTableRow-root": {
    "&:last-child .MuiTableCell-root": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    paddingBottom: "16px",
    fontWeight: 400,
    "&:first-of-type": {
      borderTopLeftRadius: "8px !important",
      borderBottomLeftRadius: "8px !important"
    },
    "&:last-of-type": {
      borderTopRightRadius: "8px !important",
      borderBottomRightRadius: "8px !important"
    },
    fontSize: "12px",
    backgroundColor: "#000",
    borderBottom: `1px solid ${theme.palette.background.default}`
  }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))
const PodParticipantsTable = ({ participants = [], podIndex, isDragDisabled = false }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { leagueData, leagueId } = useSelector((store) => store?.dmv?.league)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const [openDefaulterDialog, setOpenDefaulterDialog] = useState(false)
  const openMenu = Boolean(anchorEl)
  const { isLoading } = useSelector((state) => state.app)

  const handleMenuClick = (e, row) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    setSelectedRow(row)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMarkAsDefaulter = () => {
    if (selectedRow) {
      dispatch(
        markParticipantAsDefaulterAction({
          payload: {
            participant_id: selectedRow?.id,
            league_id: selectedRow?.league
          },
          onSuccess: () => {
            dispatch(getLeagueDataAction({ leagueId: leagueId }))
          }
        })
      )
    }
    handleMenuClose()
  }

  return (
    <>
      <DeleteConfirmationDialog
        open={openDefaulterDialog}
        onClose={() => setOpenDefaulterDialog(false)}
        title={`Mark as defaulter`}
        description={`Are you sure you want to mark ${selectedRow?.user_name} as defaulter?`}
        onDelete={handleMarkAsDefaulter}
        btnText="Defaulter"
      />
      <TableContainer>
        <CustomTable>
          <TableHead>
            <TableRow>
              {podsColumns?.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}>
                  <Typography
                    variant="body2Regular"
                    sx={{
                      color: theme.palette.text.secondary,
                      opacity: 0.6,
                      textTransform: "uppercase"
                    }}>
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
              {leagueData?.status === "running" && (
                <TableCell align="center" style={{ minWidth: 100 }}>
                  <Typography
                    variant="body2Regular"
                    sx={{
                      color: theme.palette.text.secondary,
                      opacity: 0.6,
                      textTransform: "uppercase"
                    }}>
                    Actions
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <Droppable droppableId={`pod-${podIndex}`}>
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {participants?.length > 0 ? (
                  <>
                    {participants?.map((participant, index) => (
                      <Draggable
                        key={participant.id}
                        draggableId={`participant-${participant.id}`}
                        index={index}
                        isDragDisabled={isDragDisabled}>
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...(!isDragDisabled && provided.dragHandleProps)}
                            sx={{
                              display: snapshot.isDragging ? "table" : undefined,
                              minWidth: snapshot.isDragging ? "900px" : undefined,
                              backgroundColor: snapshot.isDragging ? "#1B1F21" : "transparent",
                              border: snapshot.isDragging
                                ? "1px solid rgba(255, 255, 255, 0.1)"
                                : "none",
                              borderRadius: snapshot.isDragging ? "5px" : "0",
                              overflow: snapshot.isDragging ? "hidden" : undefined,
                              cursor: isDragDisabled ? "default" : "grab",
                              boxShadow: snapshot.isDragging
                                ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                                : "none",
                              transform: snapshot.isDragging ? "rotate(-2deg)" : "none",
                              transition: snapshot.isDragging
                                ? "none"
                                : "background-color 0.15s ease",
                              "&:hover": {
                                backgroundColor: isDragDisabled
                                  ? "transparent"
                                  : "rgba(255, 255, 255, 0.02)"
                              },
                              "&:active": {
                                cursor: isDragDisabled ? "default" : "grabbing"
                              },
                              "& td": {
                                borderBottom: snapshot.isDragging ? "none !important" : undefined,
                                display: snapshot.isDragging ? "table-cell" : undefined,
                                verticalAlign: snapshot.isDragging ? "middle" : undefined,
                                backgroundColor: snapshot.isDragging ? "#1B1F21" : undefined
                              },
                              "& td:first-of-type": {
                                width: snapshot.isDragging ? "60px" : undefined,
                                minWidth: snapshot.isDragging ? "60px" : undefined,
                                borderTopLeftRadius: snapshot.isDragging
                                  ? "5px !important"
                                  : undefined,
                                borderBottomLeftRadius: snapshot.isDragging
                                  ? "5px !important"
                                  : undefined
                              },
                              "& td:nth-of-type(2)": {
                                width: snapshot.isDragging ? "350px" : undefined,
                                minWidth: snapshot.isDragging ? "350px" : undefined
                              },
                              "& td:nth-of-type(3)": {
                                width: snapshot.isDragging ? "250px" : undefined,
                                minWidth: snapshot.isDragging ? "250px" : undefined
                              },
                              "& td:nth-of-type(4)": {
                                width: snapshot.isDragging ? "150px" : undefined,
                                minWidth: snapshot.isDragging ? "150px" : undefined
                              },
                              "& td:last-of-type": {
                                borderTopRightRadius: snapshot.isDragging
                                  ? "5px !important"
                                  : undefined,
                                borderBottomRightRadius: snapshot.isDragging
                                  ? "5px !important"
                                  : undefined
                              }
                            }}>
                            <TableCell align="center">
                              <DragIndicatorIcon
                                sx={{
                                  color: theme.palette.text.secondary,
                                  opacity: isDragDisabled ? 0.2 : 0.4,
                                  transform: "revert",
                                  pointerEvents: "none",
                                  "&:hover": {
                                    opacity: isDragDisabled ? 0.2 : 0.8
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <ProfileDisplayer
                                  key={`${participant?.id}-${participant?.image_url}`}
                                  image={participant?.image_url}
                                />
                                <Typography
                                  variant="body1Regular"
                                  sx={{ color: theme.palette.text.primary }}>
                                  {participant.user_name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body1Regular"
                                sx={{ color: theme.palette.text.primary }}>
                                {participant?.restaurant_name || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body1Regular"
                                sx={{
                                  color: theme.palette.secondary.main,
                                  textTransform: "capitalize"
                                }}>
                                {participant.job_class || "N/A"}
                              </Typography>
                            </TableCell>
                            {leagueData?.status === "running" && (
                              <TableCell align="center">
                                <span
                                  onClick={(e) => handleMenuClick(e, participant)}
                                  style={{
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center"
                                  }}>
                                  <MoreHorizIcon sx={{ height: "24px", width: "24px" }} />
                                </span>
                              </TableCell>
                            )}
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </>
                ) : (
                  <TableRow>
                    <TableNoRecords
                      colSpan={podsColumns?.length}
                      label={
                        isLoading
                          ? "Please wait we are fetching the participants..."
                          : "No Participants For This Pod"
                      }
                    />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Droppable>
        </CustomTable>
      </TableContainer>
      <Backdrop sx={{ color: "#fff", zIndex: 1300 }} open={openMenu} onClick={handleMenuClose}>
        <Menu
          id="contest-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "200px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          {selectedRow?.status !== "defaulter" && (
            <StyledMenuItem onClick={() => setOpenDefaulterDialog(true)}>
              <Typography variant="body1Regular">Mark as defaulter</Typography>
            </StyledMenuItem>
          )}
        </Menu>
      </Backdrop>
    </>
  )
}

export default PodParticipantsTable
